import React from 'react';
import {
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { useCreateSupportTicketMutation } from '../../store/slices/apiSlice';
import { openMessage } from '../../store/slices/showMessageSlice';
import CustomButton from '../@extended/CustomButton';
import { EMAIL_REGEX, EMAIL_VALIDATION_MESSAGE } from '../../util/util';
import {
  PROBLEM_TYPES,
  DESCRIPTION_MAX_LENGTH,
} from '../../util/supportTicketConstants';
import { getSupportTicketCreateErrorMessage } from '../../util/supportTicketErrors';

const validationSchema = Yup.object().shape({
  userName: Yup.string().required('Name is required').trim(),
  email: Yup.string()
    .required('Email is required')
    .email('Enter a valid email')
    .matches(EMAIL_REGEX, EMAIL_VALIDATION_MESSAGE),
  organizationName: Yup.string().required('Organization name is required').trim(),
  description: Yup.string()
    .required('Description is required')
    .trim()
    .max(DESCRIPTION_MAX_LENGTH, `Description must be at most ${DESCRIPTION_MAX_LENGTH} characters`),
  problemType: Yup.string().nullable(),
});

const SupportTicketForm = ({ onSuccess, onClose, initialValues = {}, showLoginHelper = false }) => {
  const dispatch = useDispatch();
  const [createSupportTicket, { isLoading }] = useCreateSupportTicketMutation();

  const formik = useFormik({
    initialValues: {
      userName: initialValues.userName ?? '',
      email: initialValues.email ?? '',
      organizationName: initialValues.organizationName ?? '',
      description: '',
      problemType: initialValues.problemType ?? '',
    },
    validationSchema,
    onSubmit: async (values) => {
      const result = await createSupportTicket({
        userName: values.userName.trim(),
        email: values.email.trim(),
        organizationName: values.organizationName.trim(),
        description: values.description.trim(),
        problemType: values.problemType || undefined,
      });
      if (result?.error) {
        const msg = getSupportTicketCreateErrorMessage(result.error);
        dispatch(openMessage({ message: msg, messageSeverity: 'error' }));
        return;
      }
      dispatch(openMessage({ message: 'Support ticket submitted successfully.', messageSeverity: 'success' }));
      formik.resetForm();
      onSuccess?.();
      onClose?.();
    },
  });

  const handleFormSubmit = (e) => {
    e.preventDefault();
    formik.handleSubmit();
  };

  const descLen = (formik.values.description ?? '').length;
  const descHelper = formik.touched.description && formik.errors.description
    ? formik.errors.description
    : `${descLen} / ${DESCRIPTION_MAX_LENGTH}`;

  return (
    <form onSubmit={handleFormSubmit}>
      <Grid container spacing={2}>
        {showLoginHelper && (
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              If you forgot your password, use the Forgot password? link below.
            </Typography>
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography variant="body2" color="#5d5d5d" sx={{ pb: 0.5 }}>
            Name
          </Typography>
          <TextField
            name="userName"
            fullWidth
            placeholder="Your name"
            value={formik.values.userName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.userName && Boolean(formik.errors.userName)}
            helperText={formik.touched.userName && formik.errors.userName}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" color="#5d5d5d" sx={{ pb: 0.5 }}>
            Email
          </Typography>
          <TextField
            name="email"
            fullWidth
            placeholder="your@email.com"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" color="#5d5d5d" sx={{ pb: 0.5 }}>
            Organization name
          </Typography>
          <TextField
            name="organizationName"
            fullWidth
            placeholder="Organization"
            value={formik.values.organizationName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.organizationName && Boolean(formik.errors.organizationName)}
            helperText={formik.touched.organizationName && formik.errors.organizationName}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" color="#5d5d5d" sx={{ pb: 0.5 }}>
            Problem type
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              name="problemType"
              value={formik.values.problemType}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              displayEmpty
              renderValue={(v) => v || 'Select (optional)'}
            >
              <MenuItem value="">None</MenuItem>
              {PROBLEM_TYPES.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" color="#5d5d5d" sx={{ pb: 0.5 }}>
            Description
          </Typography>
          <TextField
            name="description"
            fullWidth
            multiline
            rows={4}
            placeholder="Describe your issue..."
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={descHelper}
            inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
          />
        </Grid>
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1 }}>
          {onClose && (
            <Button variant="outlined" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
          )}
          <CustomButton type="submit" disabled={isLoading} sx={{ minWidth: 120 }}>
            {isLoading ? 'Submitting...' : 'Submit'}
          </CustomButton>
        </Grid>
      </Grid>
    </form>
  );
};

export default SupportTicketForm;
